import type { Area } from "react-easy-crop";

import type { CropAreaPixels, EditorState, RenderedImage } from "../types/image";

const DEFAULT_FILENAME = "horizon-vision-image";

export const defaultEditorState: EditorState = {
  crop: { x: 0, y: 0 },
  cropAreaPixels: null,
  zoom: 1,
  rotation: 0,
  flipHorizontal: false,
  flipVertical: false,
  brightness: 100,
  contrast: 100,
  saturation: 100,
  blur: 0,
  sharpen: 0,
  aspectPreset: "free",
  compareMode: "after"
};

export function getAspectRatioValue(aspectPreset: EditorState["aspectPreset"]): number | undefined {
  switch (aspectPreset) {
    case "1:1":
      return 1;
    case "4:3":
      return 4 / 3;
    case "16:9":
      return 16 / 9;
    default:
      return undefined;
  }
}

export function buildCssFilter(editorState: EditorState): string {
  return [
    `brightness(${editorState.brightness}%)`,
    `contrast(${editorState.contrast}%)`,
    `saturate(${editorState.saturation}%)`,
    `blur(${editorState.blur}px)`
  ].join(" ");
}

export function hasMeaningfulEdits(editorState: EditorState): boolean {
  return (
    editorState.zoom !== 1 ||
    editorState.rotation !== 0 ||
    editorState.flipHorizontal ||
    editorState.flipVertical ||
    editorState.brightness !== 100 ||
    editorState.contrast !== 100 ||
    editorState.saturation !== 100 ||
    editorState.blur !== 0 ||
    editorState.sharpen !== 0 ||
    editorState.aspectPreset !== "free" ||
    editorState.crop.x !== 0 ||
    editorState.crop.y !== 0
  );
}

export function sanitizeCropArea(area: Area | CropAreaPixels | null, fallbackWidth: number, fallbackHeight: number): CropAreaPixels {
  if (!area) {
    return {
      x: 0,
      y: 0,
      width: fallbackWidth,
      height: fallbackHeight
    };
  }

  return {
    x: Math.max(0, Math.round(area.x)),
    y: Math.max(0, Math.round(area.y)),
    width: Math.max(1, Math.round(area.width)),
    height: Math.max(1, Math.round(area.height))
  };
}

export async function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    return { width: image.naturalWidth, height: image.naturalHeight };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
}

export function buildExportFileName(sourceName: string): string {
  const sanitizedName = sourceName.replace(/\.[^.]+$/, "");
  return `${sanitizedName || DEFAULT_FILENAME}-edited.png`;
}

function getRadianAngle(rotation: number): number {
  return (rotation * Math.PI) / 180;
}

function rotateSize(width: number, height: number, rotation: number): { width: number; height: number } {
  const radians = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(radians) * width) + Math.abs(Math.sin(radians) * height),
    height: Math.abs(Math.sin(radians) * width) + Math.abs(Math.cos(radians) * height)
  };
}

function loadImage(sourceUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () => reject(new Error("Failed to load image.")));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = sourceUrl;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to create image blob."));
        return;
      }
      resolve(blob);
    }, mimeType);
  });
}

function clamp(value: number, min = 0, max = 255): number {
  return Math.min(max, Math.max(min, value));
}

function applySharpen(canvas: HTMLCanvasElement, intensity: number): void {
  if (intensity <= 0) {
    return;
  }

  const context = canvas.getContext("2d");
  if (!context) {
    return;
  }

  const { width, height } = canvas;
  const sourceImage = context.getImageData(0, 0, width, height);
  const outputImage = context.createImageData(width, height);
  const source = sourceImage.data;
  const output = outputImage.data;
  const weight = intensity * 0.45;

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const offset = (y * width + x) * 4;
      const neighbors = [
        ((y - 1) * width + x) * 4,
        ((y + 1) * width + x) * 4,
        (y * width + (x - 1)) * 4,
        (y * width + (x + 1)) * 4
      ];

      for (let channel = 0; channel < 3; channel += 1) {
        const original = source[offset + channel];
        const average =
          neighbors.reduce((sum, neighborOffset) => sum + source[neighborOffset + channel], 0) / neighbors.length;
        output[offset + channel] = clamp(original + (original - average) * weight);
      }

      output[offset + 3] = source[offset + 3];
    }
  }

  context.putImageData(outputImage, 0, 0);
}

export async function renderEditedImage(sourceUrl: string, editorState: EditorState): Promise<RenderedImage> {
  const image = await loadImage(sourceUrl);
  const cropArea = sanitizeCropArea(editorState.cropAreaPixels, image.naturalWidth, image.naturalHeight);
  const radians = getRadianAngle(editorState.rotation);
  const bounds = rotateSize(image.naturalWidth, image.naturalHeight, editorState.rotation);

  const sourceCanvas = document.createElement("canvas");
  const sourceContext = sourceCanvas.getContext("2d");
  if (!sourceContext) {
    throw new Error("Unable to prepare source canvas.");
  }

  sourceCanvas.width = Math.ceil(bounds.width);
  sourceCanvas.height = Math.ceil(bounds.height);

  sourceContext.translate(sourceCanvas.width / 2, sourceCanvas.height / 2);
  sourceContext.rotate(radians);
  sourceContext.scale(editorState.flipHorizontal ? -1 : 1, editorState.flipVertical ? -1 : 1);
  sourceContext.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

  const outputCanvas = document.createElement("canvas");
  const outputContext = outputCanvas.getContext("2d");
  if (!outputContext) {
    throw new Error("Unable to prepare output canvas.");
  }

  outputCanvas.width = cropArea.width;
  outputCanvas.height = cropArea.height;
  outputContext.filter = buildCssFilter(editorState);
  outputContext.drawImage(
    sourceCanvas,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    cropArea.width,
    cropArea.height
  );

  applySharpen(outputCanvas, editorState.sharpen / 100);

  const blob = await canvasToBlob(outputCanvas, "image/png");
  return {
    blob,
    objectUrl: URL.createObjectURL(blob),
    updatedAt: new Date().toISOString()
  };
}
